/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Page Help
 */
import Fuse from 'fuse.js';
import _ from 'lodash';
import { Button, Col, Drawer, Input, Row, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';

import FAQList, { prepareAllFaq, Item } from 'components/subscriptions/userHelp/FAQList';
import useServiceTypes from 'hooks/eligibility/useServiceTypes';
import { HeaderIcons } from 'components/app/header/Icons';
import { useAppStoreValues } from 'store/app';

interface PageHelpProps {
	item: Menu.Item;
}

interface IState {
	visible: boolean;
	searchText: string;
	searchResults: Item[];
	collapseKey: number;
}

const defaultState: IState = {
	visible: false,
	searchText: '',
	searchResults: [],
	collapseKey: Date.now(),
};

/**
 * fuse js config & init
 */
const fuse = new Fuse<Faq.ISchema>([], {
	keys: ['question', 'answerString', 'key'],
	includeScore: true,
	includeMatches: true,
	minMatchCharLength: 2,
	shouldSort: true,
	findAllMatches: true,
	ignoreLocation: true,
	threshold: 0.3,
});

/**
 * Page Help
 */
const PageHelp: React.FC<PageHelpProps> = (props) => {
	const { setupInfo } = useAppStoreValues();
	const [metaData, setMetaData] = useState(defaultState);
	const { data: serviceTypes } = useServiceTypes();
	const { visible, searchText, searchResults, collapseKey } = metaData;

	const allFaq = useMemo(
		() =>
			prepareAllFaq({
				supportEmail: setupInfo.supportEmail,
				serviceTypes: serviceTypes || [],
				faqOrder: props.item?.faqCategories,
			}),
		[props.item?.faqCategories, setupInfo, serviceTypes],
	);

	const openModal = () => {
		setMetaData({ ...metaData, visible: true, searchText: '', searchResults: allFaq });
	};

	const closeModal = () => setMetaData({ ...metaData, visible: false });

	const setSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
		setMetaData({ ...metaData, searchText: e.target.value || '' });
	};

	const clearSearch = () => {
		setMetaData({
			...metaData,
			searchText: '',
			searchResults: allFaq,
			collapseKey: Date.now(),
		});
	};

	const setSearchResults = (match: string) => {
		if (match) {
			const results = _.map(allFaq, (category) => {
				// answers
				fuse?.setCollection(category?.answers);
				const filteredAnswers = _.map(fuse?.search(match), 'item');
				// videos
				fuse?.setCollection(category?.videos);
				const filteredVideos = _.map(fuse?.search(match), 'item');
				// pick no empty results
				if (!_.isEmpty(filteredAnswers) || !_.isEmpty(filteredVideos)) {
					return { ...category, answers: filteredAnswers, videos: filteredVideos };
				}
			});
			setMetaData({ ...metaData, searchResults: _.compact(results), collapseKey: Date.now() });
		}
		// reset to all faqs
		else {
			clearSearch();
		}
	};

	// TODO highlight answer & optimise fuse initialization
	return (
		<>
			<Tooltip title={'View Help Section'}>
				<HeaderIcons.Help onClick={openModal} />
			</Tooltip>
			{visible && (
				<Drawer open={visible} title={'Help Section'} onClose={closeModal} width='40vw'>
					<Row gutter={[0, 24]}>
						<Col span={24}>
							<Input.Search
								className='tw-bordered-search-input'
								value={searchText}
								placeholder='Enter your search phrase here'
								allowClear={{
									clearIcon: (
										<Button type='link' size='small' className='h-5.25' onClick={clearSearch}>
											{'clear'}
										</Button>
									),
								}}
								onChange={setSearchText}
								onSearch={(match) => setTimeout(() => setSearchResults(match))}
							/>
						</Col>
						<Col span={24}>
							<FAQList schema={searchResults} searchText={searchText} collapseKey={collapseKey} />
						</Col>
					</Row>
				</Drawer>
			)}
		</>
	);
};

export default withErrorBoundary(PageHelp, { FallbackComponent: () => <HeaderIcons.Help /> });
